'use client';

import Image from 'next/image';
import type { MouseEvent } from 'react';

import type { ProviderProfileLinkType } from '@paction-2.0/util/constants/providerProfileLinks';

import type { PresentedSocialLink } from '@/features/catalog/providers/provider.presenter';

import styles from './page.module.scss';

const PROFILE_LINK_CLICKS_URL = '/api/v1.0/public/provider-profile-links';
const BEACON_CONTENT_TYPE = 'text/plain;charset=UTF-8';
const MIDDLE_MOUSE_BUTTON = 1;

const SOCIAL_ICON_SRC: Record<ProviderProfileLinkType, string> = {
  website: '/icons/social-website.svg',
  facebook: '/icons/social-facebook.svg',
  instagram: '/icons/social-instagram.svg',
  tiktok: '/icons/social-tiktok.svg',
  youtube: '/icons/social-youtube.svg'
};

const sendProfileLinkClick = (
  providerId: number,
  link: ProviderProfileLinkType
) => {
  try {
    const body = JSON.stringify({ providerId, link });
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(
        PROFILE_LINK_CLICKS_URL,
        new Blob([body], { type: BEACON_CONTENT_TYPE })
      )
    ) {
      return;
    }
    void fetch(PROFILE_LINK_CLICKS_URL, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'content-type': BEACON_CONTENT_TYPE }
    }).catch(() => undefined);
  } catch (_error) {
    return;
  }
};

type ProfileSocialLinksProps = {
  providerId: number;
  links: PresentedSocialLink[];
};

export function ProfileSocialLinks({
  providerId,
  links
}: ProfileSocialLinksProps) {
  if (links.length === 0) return null;

  return (
    <div className={styles.socialLinks} aria-label="Profile links">
      {links.map((link) => (
        <a
          key={link.icon}
          href={link.href}
          className={styles.socialLink}
          aria-label={link.label}
          title={link.label}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={() => sendProfileLinkClick(providerId, link.icon)}
          onAuxClick={(event: MouseEvent<HTMLAnchorElement>) => {
            if (event.button === MIDDLE_MOUSE_BUTTON) {
              sendProfileLinkClick(providerId, link.icon);
            }
          }}
        >
          <Image
            src={SOCIAL_ICON_SRC[link.icon]}
            alt=""
            width={20}
            height={20}
            aria-hidden="true"
          />
        </a>
      ))}
    </div>
  );
}
